import axios from 'axios'
import { getCustomerId } from 'utils/auth'
import { loadAuthToken } from './auth'
import { JUST_SESSION_EXPIRED_KEY } from 'constants/sessionStorage'
import { AUTH_ERROR_TYPES } from 'constants/errorCodes'
import { FEATURE_FLAG_FACTSET_KONG_LIMA } from 'constants/featureFlag'
import unleashClient from 'services/unleashClient'
import { getValidOidcUser } from 'auth/factsetOidc'
import { factset } from 'config'
import { setOidcTokenConfirmed } from 'config/apiBaseUrl'

/**
 * Handles a rejected API response: redirects FactSet users to the session-expired
 * interstitial on 401, or — for Kong/LIMA users — refreshes the OIDC token and retries
 * the request when a network error (Kong's CORS response to an expired token) is seen.
 * @param {import('axios').AxiosError} error - The error from axios
 * @returns {Promise<never>} Always rejects or suspends — never resolves with a value
 */
async function handleAuthenticationError(error) {
  if (error?.response?.status === 401) {
    const errors = error.response.data?.errors || []
    if (
      errors.some(
        e =>
          e.code === AUTH_ERROR_TYPES.identityLocked || e.code === AUTH_ERROR_TYPES.notConfigured,
      )
    ) {
      throw error
    }

    // Redirect to the session-expired interstitial.
    sessionStorage.setItem(JUST_SESSION_EXPIRED_KEY, 'true')
    window.location.href = window.location.origin
    // Never resolves — keeps callers suspended while the browser navigates away,
    // preventing a null-response crash that would trigger an error boundary.
    return new Promise(() => {})
  }

  // Kong/LIMA: a network error (no response at all — Kong returns a CORS error, not a 401)
  // may mean the OIDC token expired. Refresh it and retry, or let signinRedirect take over.
  if (
    !error?.response &&
    error.code === 'ERR_NETWORK' &&
    factset.oidcAuthority &&
    factset.oidcClientId &&
    unleashClient().isEnabled(FEATURE_FLAG_FACTSET_KONG_LIMA)
  ) {
    try {
      const user = await getValidOidcUser()
      // null means signinRedirect was called — suspend until the browser navigates away
      if (!user) {
        return new Promise(() => {})
      }
      setOidcTokenConfirmed(true)
      const retryConfig = {
        ...error.config,
        headers: { ...error.config.headers, Authorization: `Bearer ${loadAuthToken()}` },
      }
      return axios(retryConfig).then(response =>
        retryConfig._rawResponse ? response : response.data,
      )
    } catch {
      // signinRedirect threw — fall through to the plain rethrow below
    }
  }

  throw error
}

/**
 * @param {string} url - Base URL for the API
 * @param {boolean} [rawResponse=false] - Return raw axios response instead of response.data
 * @param {object} [options] - Optional configuration
 * @param {boolean} [options.skipAuthRedirect=false] - When true, a 401 propagates as a plain rejection
 *   instead of triggering the auth error handler (redirect to login) — use for logout where a redirect would block the flow
 * @returns {import('axios').AxiosInstance} Configured axios instance
 */
function httpClient(url, rawResponse = false, { skipAuthRedirect = false } = {}) {
  const token = loadAuthToken()
  const customerId = getCustomerId()
  const customerIdHeader = customerId ? { 'X-Irwin-Customer-Id': customerId } : {}
  const authHeader = token ? { Authorization: `Bearer ${token}` } : {}

  // TODO: remove X-Irwin-New-Auth when Auth0 is fully removed
  const newAuthHeader = { 'X-Irwin-New-Auth': 'true' }

  const featureFlags = unleashClient()
    .getAllToggles()
    .filter(toggle => toggle.enabled)
    .map(toggle => toggle.name)
  const featureFlagsHeader = { 'X-Irwin-Feature-Flags': featureFlags.join(',') }

  const API = axios.create({
    baseURL: url,
    responseType: 'json',
    headers: { ...authHeader, ...customerIdHeader, ...newAuthHeader, ...featureFlagsHeader },
    _rawResponse: rawResponse,
  })

  API.interceptors.response.use(
    response => (rawResponse ? response : response.data),
    skipAuthRedirect ? undefined : handleAuthenticationError,
  )

  return API
}

export { httpClient }
